import { createConsumer, type Consumer } from '@rails/actioncable'

// Consumer único para toda la app. A diferencia de kyb (que pasa el token en la
// URL desde el store), orcus autentica el socket con la cookie httpOnly
// `_orcus_jwt`, que viaja sola en el handshake porque el cable se monta bajo
// /api (mismo path que la cookie).
let userConsumer: Consumer | null = null

export function getUserCable(): Consumer {
  userConsumer ??= createConsumer('/api/cable')
  return userConsumer
}

export function resetUserCable() {
  userConsumer?.disconnect()
  userConsumer = null
}

// Interfaz interna de ActionCable: el Consumer expone su conexion, pero el paquete
// no publica tipos, asi que la declaramos con lo minimo que necesitamos.
interface ProbableConsumer extends Consumer {
  connection: { isOpen: () => boolean }
}

/**
 * Levanta el cable y responde si quedo abierto antes del timeout.
 *
 * Es la unica forma de saber si la red del usuario deja pasar WebSockets: los
 * proxies corporativos con inspeccion TLS bloquean el header `Upgrade`, y el
 * browser no dice por que fallo. Se corre una vez por sesion (al autenticarse)
 * y el resultado viaja como parametro en los requests que podrian streamear.
 *
 * Si el cable no levanta hay que bajarlo: `connection.open()` arranca el
 * ConnectionMonitor de ActionCable, que reabre el socket cada ~6-25s mientras
 * siga muerto. Sin el `resetUserCable()`, el usuario detras del proxy —o sea,
 * justo el que este probe existe para detectar— se pasa toda la sesion
 * reintentando un handshake que su red nunca va a dejar pasar.
 *
 * El timeout esta en el camino critico de la primera generacion de la sesion:
 * un handshake que no cerro en 1.5s no va a cerrar.
 */
export function probeUserCable(timeoutMs = 1500): Promise<boolean> {
  const consumer = getUserCable() as ProbableConsumer
  consumer.connect()

  return new Promise((resolve) => {
    let elapsed = 0
    const step = 100
    const tick = () => {
      if (consumer.connection.isOpen()) return resolve(true)
      elapsed += step
      if (elapsed >= timeoutMs) {
        resetUserCable()
        return resolve(false)
      }
      setTimeout(tick, step)
    }
    tick()
  })
}

interface SocketBroadcastData {
  type: string
  [key: string]: unknown
}

type ReceivedCallback = (data: SocketBroadcastData) => void

export function subscribeUserChannel(
  channel: string,
  params: Record<string, string>,
  onReceived: ReceivedCallback,
) {
  return getUserCable().subscriptions.create({ channel, ...params }, { received: onReceived })
}
